/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import MsDyn365, { getUrlSync } from '@msdyn365-commerce/core';
import { IHeaderViewProps, Logo } from '@msdyn365-commerce-modules/header';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node, addThrottledEvent, VariantType, isMobile } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';

/**
 * GridSizes.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface IState {
    isTablet: boolean;
    headerHeight?: number;
}

/**
 *
 * HeaderView component.
 * @extends {React.PureComponent<IHeaderViewProps>}
 */
export class HeaderView extends React.PureComponent<IHeaderViewProps & IHeaderProps<{}>, IState> {
    private readonly menuNode: React.RefObject<HTMLDivElement>;
    private customResizeThrottledEventHandler?: (event: Event) => void;

    public constructor(props: IHeaderViewProps & IHeaderProps<{}>) {
        super(props);
        this.menuNode = React.createRef();
        this.state = {
            isTablet: false,
            headerHeight: 104
        };
    }

    public componentDidMount(): void {
        this.customResizeThrottledEventHandler = addThrottledEvent(window, 'resize', this._customUpdateViewport as EventListener);
        this._customUpdateViewport();
        if (this.props.config.useStickyHeader) {
            // Set header update methods to trigger on scroll
            if (MsDyn365.isBrowser) {
                window.addEventListener('scroll', () => {
                    this._configureStickyHeader();
                });
            }
            this._configureStickyHeader();
        }

        if (MsDyn365.isBrowser) {
            const getHeaderHeight = document.getElementsByClassName('ms-header').item(0)?.clientHeight;
            this.setState({ headerHeight: getHeaderHeight });
            document.documentElement.style.setProperty('--headerHeight', `${this.state.headerHeight}px`);
        }
    }

    public componentDidUpdate(prevProps: {}, prevState: IState): void {
        if (MsDyn365.isBrowser && prevState.headerHeight !== this.state.headerHeight) {
            document.documentElement.style.setProperty('--headerHeight', `${this.state.headerHeight}px`);
        }
    }

    public componentWillUnmount(): void {
        window.removeEventListener('resize', this.customResizeThrottledEventHandler!, false);
    }

    public render(): JSX.Element | null {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            HeaderTag,
            HeaderContainer,
            HeaderTopBarContainer,
            Divider,
            MobileMenuContainer,
            MobileMenuBodyContainer,
            MobileMenuLinksContainer,
            signInLink,
            signOutLink
        } = this.props;

        HeaderTag.tag = 'div';

        let signinUrl = this.props.context.request.user.signInUrl;
        let signoutUrl = this.props.context.request.user.signOutUrl;
        const customUrl = getUrlSync('/redirecting', this.props.context.actionContext);
        const homeUrl = getUrlSync('/home', this.props.context.actionContext);
        const origin = this.props.context.request.url.requestUrl.origin;
        // const homepageUrl = `ru=${origin + homeUrl}`;
        const customPageUrl = `ru=${origin + customUrl}`;

        if (this.props.config.isGuestHeader && this.props.context.request.user.isAuthenticated) {
            window.location.href = origin + homeUrl;
        }
        signinUrl = `${signinUrl}${signinUrl?.includes('?') ? `&${customPageUrl}` : `?${customPageUrl}`}`;
        signoutUrl = `${signoutUrl}${signoutUrl?.includes('?') ? `&${customPageUrl}` : `?${customPageUrl}`}`;

        const signinLinkOverride =
            signInLink &&
            React.cloneElement(signInLink as JSX.Element, {
                href: signinUrl
            });

        const signoutLinkOverride =
            signOutLink &&
            React.cloneElement(signOutLink as JSX.Element, {
                href: signoutUrl
            });
        const { retailCustomerGroupList } = this.props.context.app.config;
        const customerGroup = this.props.data.accountInformation.result?.CustomerGroup;
        return (
            <Module {...HeaderTag}>
                <Node {...HeaderContainer}>
                    {this.props.context.request.user.isAuthenticated && retailCustomerGroupList?.includes(customerGroup) ? (
                        <Node {...HeaderTopBarContainer}>
                            <div className='desktop-navicon-container' ref={this.menuNode}>
                                {this.state.isTablet ? <></> : this._renderReactFragment(this.props.menuBar)}
                            </div>
                            {this.props.logo}
                            <div className='mobile-navicon-container'>{this.props.navIcon}</div>
                            {this._renderMobileLogo()}
                            <Node className='desktop-navicon-right-container'>
                                {this._renderReactFragment(this.props.search)}
                                {this.props.preferredStore}
                                {this._renderAccountBlock(this.props, signinLinkOverride, signoutLinkOverride)}
                                <Node {...Divider} />
                                {this.props.cartIcon}
                                {this._renderReactFragment(this.props.siteOptions)}
                            </Node>
                        </Node>
                    ) : (
                        <Node {...HeaderTopBarContainer}>
                            {this.props.logo}
                            <div className='desktop-navicon-container' ref={this.menuNode}>
                                {this.state.isTablet ? <></> : this._renderReactFragment(this.props.menuBar)}
                            </div>
                            <div className='mobile-navicon-container'>{this.props.navIcon}</div>
                            {this._renderMobileLogo()}
                            {this._renderReactFragment(this.props.search)}
                            {this.props.preferredStore}
                            {this._renderAccountBlock(this.props, signinLinkOverride, signoutLinkOverride)}
                            <Node {...Divider} />
                            {this.props.cartIcon}
                            {this._renderReactFragment(this.props.siteOptions)}
                        </Node>
                    )}

                    <Node {...MobileMenuContainer}>
                        <Node {...MobileMenuBodyContainer}>
                            {this.props.MobileMenuHeader}
                            {this._renderReactFragment(this.props.menuBar)}
                            <Node {...MobileMenuLinksContainer}>
                                {this.props.accountLinks ? this.props.accountLinks.map(link => link) : false}
                                {this.props.siteOptions}
                                {this.props.wishListIconMobile}
                                {signinLinkOverride && signinLinkOverride}
                                {signoutLinkOverride && signoutLinkOverride}
                            </Node>
                        </Node>
                    </Node>
                    <div className='desktop-navicon-container' ref={this.menuNode}>
                        {this.state.isTablet ? this._renderReactFragment(this.props.menuBar) : <></>}
                    </div>
                </Node>
            </Module>
        );
    }

    /**
     * Function to update header.
     */
    private readonly _configureStickyHeader = (): void => {
        // Get heights of cookie and promotion banners
        const defaultValue = 0;
        const headerAlertsContainer: HTMLElement | null = document.querySelector('.ms-promo-banner');
        const bannerHeights = headerAlertsContainer ? headerAlertsContainer.offsetHeight : defaultValue;

        // Triggers opacity change of header
        const headerElement = document.querySelector('.ms-header .default-container');
        if (headerElement) {
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > defaultValue) {
                headerElement.classList.add('lock-opaque');
                headerElement.classList.add('fixed');
            } else {
                headerElement.classList.remove('lock-opaque');
                headerElement.classList.remove('fixed');
            }
        }

        // Update sticky header position and opacity
        const stickyHeader: HTMLElement | null = document.querySelector('.desktop-navicon-container');
        const headerLogo: HTMLElement | null = document.querySelector('.ms-header__logo');

        if (stickyHeader && headerLogo) {
            // Fix center sticky header
            const navStickyPos = headerLogo.offsetHeight + bannerHeights;
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > navStickyPos) {
                stickyHeader.classList.add('fixed');
            } else {
                stickyHeader.classList.remove('fixed');
            }
        }
    };

    private _renderMobileLogo(): JSX.Element | null {
        const { config, context, typeName } = this.props;
        return (
            <Logo
                {...{
                    link: config.logoLink,
                    image: config.mobileLogoImage ?? {},
                    className: 'ms-header__mobile-logo',
                    gridSettings: context.request.gridSettings,
                    requestContext: context.request,
                    typeName
                }}
            />
        );
    }

    /**
     * Function to render account block.
     * @param props - IHeaderViewProps.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderAccountBlock = (
        props: IHeaderViewProps,
        signinLinkOverride?: React.ReactNode,
        signoutLinkOverride?: React.ReactNode
    ): JSX.Element | null => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            AccountInfoDropdownParentContainer,
            AccountInfoDropdownPopoverConentContainer,
            accountInfoDropdownButton,
            // signOutLink,
            accountLinks
        } = props;
        if (AccountInfoDropdownParentContainer) {
            const accountClassName = classnames(AccountInfoDropdownParentContainer.className, 'account-desktop');
            if (AccountInfoDropdownPopoverConentContainer) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {accountInfoDropdownButton}
                        <Node {...AccountInfoDropdownPopoverConentContainer}>
                            {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                            {signoutLinkOverride && signoutLinkOverride}
                        </Node>
                    </Node>
                );
            } else if (signinLinkOverride) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {signinLinkOverride}
                    </Node>
                );
            }
        }
        return null;
    };

    /**
     * Function to render react fragment.
     * @param items - React nodes.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderReactFragment = (items: React.ReactNode[]): JSX.Element | null => {
        return (
            <>
                {ArrayExtensions.hasElements(items)
                    ? items.map((slot: React.ReactNode) => {
                          return <>{slot}</>;
                      })
                    : null}
            </>
        );
    };
    private readonly _customUpdateViewport = (): void => {
        const viewport = isMobile({ variant: VariantType.Browser, context: this.props.context.request });
        const isTabletView = viewport === 'sm' || viewport === 'md';
        if (MsDyn365.isBrowser) {
            const getHeaderHeight = document.getElementsByClassName('ms-header').item(0)?.clientHeight;
            this.setState({ headerHeight: getHeaderHeight });
        }
        this.setState({ isTablet: isTabletView });
    };
}

export default HeaderView;
